/**
 * FOH WORKBOARD (route kept as /foh-queue so every existing link still works).
 *
 * Was the page wrapper for `FohCustodyQueue`; the queue now lives on as the
 * Workboard's Needs Attention lane, fed by the same read as the other lanes
 * (one list, never two that can disagree). The page is flag-gated by
 * `custodyHandoff`; the route itself is role-gated to the FOH-capable
 * roles (`OWNER | MANAGER | SERVICE_WRITER | ORG_ADMIN`) per spec §6.1.
 *
 * Display-only with respect to scheduling.
 */
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { useTicketCustodyRealtime } from '@/hooks/tickets/useTicketCustodyRealtime';
import { FohWorkboard } from '@/components/foh/workboard/FohWorkboard';
import { FohAttentionCapacityCard } from '@/components/foh/FohAttentionCapacityCard';
import { useFohAttentionCapacity } from '@/hooks/foh/useFohAttentionCapacity';
import { TopBar } from '@/components/layout/TopBar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export default function FohQueue() {
  const { activeShop } = useAuth();
  const custodyHandoffEnabled = useFeatureFlag('custodyHandoff');
  // CORE-BN — the shop-level attention verdict sits ABOVE the queue on
  // purpose. The capability record's accessibility finding is that under
  // saturation a user cannot afford to infer the shop's state by counting
  // rows, so the state has to be stated before the rows begin.
  const fohAttentionEnabled = useFeatureFlag('fohAttentionCapacity');
  const fohAttention = useFohAttentionCapacity({
    shopId: activeShop?.id ?? null,
    enabled: fohAttentionEnabled && custodyHandoffEnabled,
  });
  // Slice 4A.2: live updates from ticket_custody changes. The hook is
  // a no-op when the flag is off or shopId is missing, so the early
  // returns below remain safe (hook order is preserved).
  useTicketCustodyRealtime(activeShop?.id ?? null, custodyHandoffEnabled);

  if (!custodyHandoffEnabled) {
    return (
      <>
        <TopBar />
        <div className="container mx-auto p-4 md:p-6 max-w-5xl">
          <Card>
            <CardHeader>
              <CardTitle>FOH Workboard</CardTitle>
              <CardDescription>
                This feature is not enabled for this shop.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Ask an internal admin to enable <code>custodyHandoff</code> at{' '}
                <code>/feature-flags</code>.
              </p>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  if (!activeShop?.id) {
    return (
      <>
        <TopBar />
        <div className="container mx-auto p-4 md:p-6 max-w-5xl">
          <Card>
            <CardHeader>
              <CardTitle>FOH Workboard</CardTitle>
              <CardDescription>No active shop selected.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <TopBar />
      <div className="mx-auto p-3 md:p-6 max-w-[1800px] space-y-4">
        <h1 className="text-xl font-semibold" data-testid="foh-workboard-title">FOH Workboard</h1>
        {fohAttentionEnabled && (
          <FohAttentionCapacityCard
            result={fohAttention.result}
            summary={fohAttention.summary}
            isLoading={fohAttention.isLoading}
          />
        )}
        <FohWorkboard shopId={activeShop.id} />
      </div>
    </>
  );
}
